import { getTextLocalizer, Localizer } from '@sas/nova-core/i18n';
import { useRef, useState } from 'react';
import { InputChangeEvent, InputCommitEvent } from '../../../Input';
import bundle from '../../NovaComponents-ColorPicker-gui-icu.properties';
import { addBundle } from '../../../../locale/createUseTextLocalizer';

addBundle(bundle);
let localize: Localizer;
getTextLocalizer('NovaComponents-ColorPicker-gui-icu').then((localizer) => {
  localize = localizer;
});

export interface ColorValidationProps {
  initialValue?: string;
  validateInputFunction?: (params: { value: string; isLive?: boolean }) => Error;
  onCommit?: (value: string) => void;
  /**
   * @default RGB
   */
  adapterType?: 'RGB' | 'HEX';
}

interface Error {
  isError?: boolean;
  message?: string;
  status?: 'alert' | 'warning';
}

export const validateRGBField = (params: { value: string }) => {
  const { value } = params;
  let isError = false,
    message: Error['message'],
    status: Error['status'];

  const rgbRegex = /^([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5])$/;
  if (
    value.length !== 0 &&
    (/^\s+$/.test(value) || isNaN(parseInt(value, 10)) || !rgbRegex.test(value))
  ) {
    isError = true;
    message = localize('customTab.rgbField.error.msg');
    status = 'alert';
  } else {
    isError = false;
    message = undefined;
    status = undefined;
  }
  return {
    isError,
    message,
    status,
  };
};

export const validateHexField = (params: { value: string; isLive?: boolean }) => {
  const { value, isLive } = params;
  let isError = false,
    message: Error['message'],
    status: Error['status'];

  if (isLive && /[^0-9a-fA-F]+/.test(value)) {
    isError = true;
    message = localize('customTab.hexField.error.msg');
    status = 'alert';
  } else if (!isLive && value.length !== 0 && !/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)) {
    isError = true;
    message = localize('customTab.hexField.error.msg');
    status = 'alert';
  } else {
    isError = false;
    message = undefined;
    status = undefined;
  }
  return {
    isError,
    message,
    status,
  };
};

export const useColorValidationAdapter = (props: ColorValidationProps) => {
  const [value, setValue] = useState(props.initialValue ?? '');
  const [error, setError] = useState<Error>({
    isError: false,
    message: undefined,
    status: undefined,
  });
  const preservedValue = useRef<string>(value);
  const adapterType = useRef<ColorValidationProps['adapterType']>(props.adapterType ?? 'RGB');

  const handleChange = (event: InputChangeEvent) => {
    setValue(event.value);
    const errors = props.validateInputFunction?.({ value: event.value, isLive: true });
    setError(errors ?? { isError: false, message: undefined, status: undefined });
  };

  const handleBlur = () => {
    let formattedValue: string;

    if (adapterType.current === 'RGB') {
      if (error.isError) return;

      if (value.length === 0) {
        formattedValue = preservedValue.current;
      } else {
        formattedValue = parseInt(value, 10).toString();
      }
      props.onCommit?.(formattedValue);
      preservedValue.current = formattedValue;
      setValue(formattedValue);
    }

    if (adapterType.current === 'HEX') {
      if (error.isError) return;

      const errorOnBlur = props.validateInputFunction?.({ value, isLive: false });
      if (errorOnBlur?.isError) {
        setError(errorOnBlur);
        return;
      }

      if (value.length === 0) {
        formattedValue = preservedValue.current;
      } else if (value.length === 3) {
        formattedValue = [...value].reduce((acc, char) => acc + char.repeat(2), '').toUpperCase();
        preservedValue.current = formattedValue;
      } else if (value !== preservedValue.current) {
        formattedValue = value.toUpperCase();
        preservedValue.current = formattedValue;
      } else {
        return;
      }
      setValue(formattedValue);
      props.onCommit?.(formattedValue);
    }
  };

  const handleCommit = (e: InputCommitEvent) => {
    if (e.reason === 'blur') return;
    let formattedValue;

    if (adapterType.current === 'RGB') {
      if (value.length === 0 || error.isError) return;

      formattedValue = parseInt(value, 10).toString();
      setValue(formattedValue);
      props.onCommit?.(formattedValue);

      preservedValue.current = formattedValue;
    }

    if (adapterType.current === 'HEX') {
      if (value.length === 0 || error.isError) return;

      const errorOnCommit = props.validateInputFunction?.({ value, isLive: false });
      if (errorOnCommit?.isError) {
        setError(errorOnCommit);
        return;
      }

      if (value.length === 3) {
        formattedValue = [...value].reduce((acc, char) => acc + char.repeat(2), '').toUpperCase();
      } else if (value !== preservedValue.current) {
        formattedValue = value.toUpperCase();
      } else {
        return;
      }
      preservedValue.current = formattedValue;
      setValue(formattedValue);
      props.onCommit?.(formattedValue);
    }
  };

  const handleCancel = () => {
    let formattedValue;

    if (adapterType.current === 'RGB') {
      if (value === preservedValue.current) return false;
      if (value.length === 0 || error.isError || value !== preservedValue.current) {
        formattedValue = parseInt(preservedValue.current, 10).toString();
        setValue(formattedValue);
        error.isError && setError({ isError: false, message: undefined, status: undefined });
        return true;
      }
    }

    if (adapterType.current === 'HEX') {
      if (value === preservedValue.current) return false;
      if (error.isError || value.length === 0 || value !== preservedValue.current) {
        setValue(preservedValue.current);
        error.isError && setError({ isError: false, message: undefined, status: undefined });
        return true;
      }
    }
  };

  return {
    value,
    error,
    setValue,
    setError,
    preservedValue,
    handleChange,
    handleBlur,
    handleCommit,
    handleCancel,
  };
};
